<template>
  <div>
    <component
      :is="computedComponentName"
      v-model="shouldShowModal"
      :close-on-content-click="false"
    >
      <template v-slot:activator="{ on, attrs }">
        <div
          class="d-flex align-center justify-between filter-title"
          :class="{ 'font-weight-bold': isFilterApplied }"
          v-on="on"
          v-bind="attrs"
          >
          <div class="d-flex align-center flex-grow-1">
            <v-icon left>
              local_offer
            </v-icon>
            Brand
          </div>
          <v-icon>
            expand_more
          </v-icon>
        </div>
      </template>
      <v-card>
        <v-card-title class="d-md-none">
          Brand
        </v-card-title>

        <v-card-text class="black--text pt-md-3">
          <v-row>
            <!-- Audience Section -->
            <v-col
              v-for="kind in ['audience', 'influencer']"
              :key="kind"
              cols="12"
              md="6"
            >
              <div class="mb-1 d-flex">
                <div class="d-flex align-center flex-grow-1">
                  <v-icon class="mr-1 black--text" left>
                    {{ kind === 'audience' ? 'people' : 'volume_down' }}
                  </v-icon>

                  {{ capitalizeString(kind) }}
                </div>

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      color="black"
                      v-bind="attrs"
                      v-on="on"
                    >
                      info
                    </v-icon>
                  </template>

                  <span>
                    {{ tooltipTexts[kind] }}
                  </span>
                </v-tooltip>
              </div>

              <v-autocomplete
                v-model="form[kind]"
                :items="searchItems"
                label="Brands"
                dense
                eager
                outlined
                :append-icon="null"
                :loading="isLoadingSearchResults"
                return-object
                hide-details
                item-text="name"
                item-value="id"
                no-filter
                @change="submitForm(kind)"
                :search-input.sync="searchQuery[kind]"
                @update:search-input="doSearch(kind)"
              >
                <template v-slot:no-data>
                  <div v-if="isLoadingSearchResults" class="text-center py-3">
                    <v-progress-circular
                      :width="3"
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                  </div>
                  <div v-else class="text-center py-2">
                    No results found.
                  </div>
                </template>
              </v-autocomplete>

              <div v-if="appliedFiltersFor[kind].length" class="mt-3">
                <div
                  v-for="item in appliedFiltersFor[kind]"
                  :key="item.id"
                >
                  <div class="d-flex justify-space-between align-center pb-1 pt-3">
                    <div class="d-flex align-center">
                      <v-btn small icon @click="removeFilterItem(item)">
                        <v-icon small>
                          clear
                        </v-icon>
                      </v-btn>

                      {{ item.data.inputs.name }}
                    </div>

                    <weight-selector
                      v-if="kind === 'audience'"
                      v-model="weightInput[item.timestamp]"
                      @change="updateFilterItem(item)"
                    />
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </component>
  </div>
</template>

<script>
const WeightSelector = () => import("@/blocks/common/selectors/WeightSelector.vue")

// used throughout the code
const filterName = "brand"

const originalForm = () => ({
  audience: null,
  influencer: null
})

export default {
  name: "FilterBrand",

  components: {
    WeightSelector
  },

  data: () => ({
    form: originalForm(),

    shouldShowModal: false,
    isLoadingSearchResults: false,
    searchQuery: {
      audience: "",
      influencer: ""
    },

    // The key would be the symbol for the applied filter in VueX state
    weightInput: {},

    searchItems: [],

    _timerId: null,

    tooltipTexts: {
      audience: "Identify influencers by their audience brand affinities. We determine brand affinities of audience by analyzing posts for caption text, mentions, hashtags and location tags. You can add several brands and specify minimum percentage for each brand, your search results will be refined with influencers that have audience mentioning ANY of the specified brands.",
      influencer: "Identify influencers by their brand affinities. We determine  brand affinities of influencers by analyzing posts for caption text, mentions, hashtags and location tags. You can add several brands and your search results will be refined with influencers that have ANY of the specified brands."
    }
  }),

  computed: {
    // show the filter as a dialog for mobile dialogs
    computedComponentName() {
      return this.isDesktopDevice ? "v-menu" : "v-dialog"
    },

    // get a list of all of them
    appliedFilters() {
      return this.$store.getters["influencerDiscovery/findFiltersByType"](filterName)
    },

    appliedFiltersFor() {
      return {
        audience: this.appliedFilters.filter((item) => item.data.kind === "audience"),
        influencer: this.appliedFilters.filter((item) => item.data.kind === "influencer")
      }
    },

    // Used to bold the filter name text
    isFilterApplied() {
      return Boolean(this.appliedFilters.length)
    }
  },

  validations: {
    form: {
      audience: {},
      influencer: {}
    }
  },

  methods: {
    /**
     * Takes an item and generates the text based on kind and weight
     */
    computeFilterText({ kind, name, weight }) {
      return `${capitalizeString(kind)} Brand: ${name}` + (kind === 'audience' ? ` | >${weight * 100}%` : '')
    },

    /**
     * Push the data from either of the inputs to the filters array in Vuex Store
     *
     * @param {String} kind | Either "audience" or "influencer"
     */
    async submitForm(kind) {
      await this.$v.form[kind].$touch()

      if (this.$v.form[kind].$anyError) return

      this.$store.dispatch("influencerDiscovery/addFilter", {
        type: filterName,
        data: {
          color: "blue lighten-5",
          icon: "local_offer",
          iconColor: "blue",
          kind,
          text: this.computeFilterText({ kind, name: this.form[kind].name, weight: this.form[kind].weight }),
          inputs: {
            ...this.form[kind]
          }
        }
      })

      // reset the search query input
      this.searchQuery[kind] = ""
      // reset the form input to allow more values
      this.form[kind] = null
      // finally hide the modal
      this.shouldShowModal = false
      // reset the search results
      this.searchItems = []
    },

    /**
     * Update the value of already existing filter
     * Most probably it'd just be the weight attribute
     */
    updateFilterItem(item) {
      // Update the weight attribute
      item.data.inputs.weight = this.weightInput[item.timestamp]
      // Update the text value for filter
      item.data.text = this.computeFilterText({
        kind: item.data.kind,
        name: item.data.inputs.name,
        weight: item.data.inputs.weight
      })

      this.$store.dispatch("influencerDiscovery/updateFilter", item)
    },

    /**
     * Delete the entry from vuex store
     *
     * @param {Object} item
     */
    removeFilterItem(item) {
      this.$store.dispatch("influencerDiscovery/removeFilter", item.id)
    },

    /**
     * Make API request to fetch the select options for autocomplete
     *
     * @param {String} kind
     */
    doSearch(kind) {
      // cancel pending call
      if (this._timerId) {
        clearTimeout(this._timerId)
      }

      // delay new call 500ms
      this._timerId = setTimeout(async () => {
        // if input is empty, don't continue
        if (!this.searchQuery[kind]) return

        // if a request is being made, wait for it
        if (this.isLoadingSearchResults) return

        // show the loader while request is being made
        this.isLoadingSearchResults = true

        try {
          const response = await axios({
            url: "/api/filters/brands/?q=" + this.searchQuery[kind],
          })

          // the API responds like
          this.searchItems = response.data.map((item) => ({
            ...item,
            weight: 0.05 // the default weight value
          }))
        } catch (error) {
          // log using the helper function
          logger({ type: "Network Error", error })
        } finally {
          this.isLoadingSearchResults = false
        }
      }, 500)
    }
  },
}
</script>
